/**
 * Hungarian (`hu`) string catalogue — mirrors the key shape of `./en.ts`.
 * Brand names (`OVERVOLT`, `Sniper Rail`, `Arc Coil`, `Cryo Emitter`,
 * `Splash Mortar`, `Pulse Turret`, `Pylon`, `Flux`) stay in English; the
 * rest translates idiomatically into terse, gamey Hungarian.
 */

import type { Catalogue } from './en';

export const hu: Catalogue = {
  meta: {
    name: 'Magyar',
    code: 'HU',
  },

  towers: {
    pulse: {
      label: 'Pulse Turret',
      short: 'PULZ',
      role: 'Semleges generalista. Megbízható sebzés bárhol a pályán.',
    },
    arc: {
      label: 'Arc Coil',
      short: 'ARC',
      role: 'Hálózathoz csatolt. Erősebb Pylon-többletnél, gyengébb barnaüzemben.',
    },
    cryo: {
      label: 'Cryo Emitter',
      short: 'CRYO',
      role: 'Szűkület-festő. Kis hatókör — lassítja és törékennyé jelöli az ellenfeleket egy ponton.',
    },
    mortar: {
      label: 'Splash Mortar',
      short: 'MOZSÁR',
      role: 'Közvetett tűz. Helyezd HÁTRA az úttól — közelről holtzónája van.',
    },
    sniper: {
      label: 'Sniper Rail',
      short: 'SNIPER',
      role: 'Átütő egyenes lövés. Erős hosszú szakaszokon; sarkokban gyenge.',
    },
    pylon: {
      label: 'Pylon',
      short: 'PYLON',
      role: 'Energiaforrás. Építs eleget a tornyok fogyasztására (és Arc-barát többletet).',
    },
    wall: {
      label: 'Wall Node',
      short: 'FAL',
      role: 'Olcsó akadály — csak Maze módban. Megformázza az ellenség útvonalát.',
    },
  },

  roleHints: {
    pulse: 'Pulse Turret: megbízható alapozás — szolid sebzés bárhol az úton.',
    arc: 'Arc Coil: az energiatöbblettel nő. Építs több Pylont az erősebb láncért.',
    cryo: 'Cryo Emitter: pontos szűkület-festés. Lassítja és jelöli a néhány csempét a torlasznál.',
    mortar: 'Splash Mortar: közvetett tűz. Helyezd HÁTRA az úttól — közelről nem talál.',
    sniper: 'Sniper Rail: átütő egyenes lövés. Igazítsd hosszú egyenesre a maximális hatásért.',
    pylon: 'Power Pylon: táplálja a hálózatot. Építs eleget a tornyok ellátására.',
    wall: 'Fal: olcsó akadály — megformázza az ellenség útvonalát.',
  },

  hints: {
    build:
      'Építs tornyot: válassz egyet alul (vagy nyomd meg az 1-est), majd kattints egy izzó csempére az út mellett.',
    placed: 'Jó. Tegyél le még egyet és nyomj szóközt a hullám indításához.',
    wave1: 'A tornyok automatikusan tüzelnek. Ne hagyd, hogy az energiasáv a pirosba menjen.',
    select: 'Kattints egy letett tornyot a kijelöléshez — U a fejlesztés, S az eladás.',
    brownout: 'Barnaüzem! A tornyok lelassultak. Építs Pylont (3) a több energiához.',
  },

  contextHints: {
    overload:
      'Overload: csavard fel a következő hullámot nagyobb jutalomért — nagyobb kockázattal. Lassú indulás? Tekerd fel a gyorsabb Fluxért.',
    profileKey: 'Nyomd meg a P-t a Profilért — a Flux új tornyokat, képességeket és módokat nyit.',
    endlessMod: 'Az Endless hullám-MÓDOSÍTÓKAT vált — alkalmazkodj a felállásoddal.',
    puzzleHover:
      'Puzzle mód: kézzel készített logikai pályák látvonalat blokkoló falakkal. Teljesen más játék.',
  },

  hud: {
    buildZone: 'ÉPÍT',
    waveZone: 'HULLÁM',
    abilitiesZone: 'KÉPESSÉGEK',
    overloadZone: 'OVERLOAD',

    startWave: 'HULLÁM INDÍT',
    nextWave: 'KÖV. HULLÁM',
    pause: 'SZÜNET',
    resume: 'FOLYTAT',
    restart: 'ÚJRA',
    deployHint: 'indít [SPACE]',
    holdHint: 'szünet [SPACE]',
    rebootHint: 'reset [R]',
    nextWaveHint: 'KÖV. HULLÁM',

    audioOn: 'HANG BE',
    audioOff: 'HANG KI',

    credits: 'KREDIT',
    core: 'MAG',
    wave: 'HULLÁM',
    endless: 'ENDLESS',
    score: 'PONT',
    powerGrid: 'HÁLÓZAT',

    runMode: 'MÓD',
    difficulty: 'NEHÉZSÉG',
    profile: 'PROFIL',
    map: 'TÉRKÉP',
    clickHint: '▷ klikk',

    locked: 'ZÁRVA',
    lockedShort: 'ZÁRVA',
    lockedTooltip:
      'ZÁRVA — nyisd meg ✦{cost} Fluxért a Profilon (nyomd P-t).',
    lockedTowerNotice: '{tower} zárva',
    lockedAbility: 'Képesség zárva',
    lockedOverload: 'Overload-szint zárva',
    lockedMode: '{mode} mód zárva',
    lockedDifficulty: 'Hard nehézség zárva',

    cheatPip: '🔓 CHEAT',
    profilePip: '👤 {name}',

    overloadIdle: '— overload az 1. hullámtól érvényes —',
    overloadTier: 'TIER {tier} · {name}',
    overloadActive: '◉ AKTÍV {sec}s',

    maxLevel: 'MAX SZINT',
    upgrade: 'FEJLESZT L{level}',
    sell: 'ELAD',
    target: 'CÉL: {mode}',
    cycleTarget: 'vált · [T]',
    sellHint: '+{refund}c · [S]',
    upgradeHint: '{cost}c · [U]',
    overclock: 'OVERCLOCK',
    overclockOn: 'OVERCLOCK ✦ BE',

    // Sprint 69 (i18n-gaps-extract-s69) — extracted from render.ts / hud.ts.
    buildHintIntro: "ÉPÍTÉSI FÁZIS  //  válassz tornyot [1·2·3·4·5], rakd le és INDÍTSD A HULLÁMOT [SPACE]",
    buildHintReinforce: "ÉPÍTÉSI FÁZIS  //  erősítsd a rácsot és INDÍTSD A HULLÁMOT {wave} [SPACE]",
    mazeRoute: "L A B I R I N T U S   Ú T J A",
    tiles: "MEZŐ",
    abilityActive: "◉ AKTÍV {sec}s",
    costSuffix: '{cost}c',
    overclockPip: 'OC ×{n}',
    powerReadout: '{demand} / {supply}W  ·  {pct}%',
    towerCostDraw: '{cost}c {power}w',
    pylonCostSupply: '{cost}c +{supply}w',
    wallCost: '{cost}c 0w',
    endlessModBadge: 'MOD · {label}',
    startWaveOverload: '⚡{n} {tierName}',
    waveOverloadPip: '⚡{n} {tierName}',
    overclockNoticeOn: "{tower} Overclock BE",
    overclockNoticeOff: "{tower} Overclock KI",

    tierMult: {
      hp: "HP",
      sp: "SP",
      cn: "SZ",
      gd: "AR",
      sc: "PT",
    },
    spawnShort: {
      bit: "Bit",
      packet: "Pkt",
      daemon: "Dmn",
      fork: "Fork",
      cipher: "Cph",
      patch: "Pch",
      buffer: "Buf",
      streak: "Stk",
      kernel: "KERNEL",
    },
  },

  modes: {
    campaign: 'KAMPÁNY',
    endless: 'ENDLESS',
    maze: 'MAZE',
    puzzle: 'PUZZLE',
  },
  modeSub: {
    campaign: '10 hullám · győzelem',
    endless: '✦ {cost}',
    maze: '✦ {cost}',
    puzzle: 'pálya választás',
  },
  modeBlurb: {
    campaign: '10 hullám. Boss a 10. hullámon. Nyerj és továbblépsz.',
    endless: 'Ugyanaz a pálya. Minden hullám új módosító. Alkalmazkodj vagy átszivárognak.',
    maze: 'Nyílt mező. Falakkal te alakítod az utat. Lassítsd őket vagy fogd vissza a zsákmányt.',
    puzzle: '10 kézzel készített pálya. Találd meg az egyetlen nyertes felállást.',
  },
  modeCard: {
    campaign: {
      tagline: 'Tanuld meg a hálót, üsd ki a bosst.',
      bullet1: '10 kézzel hangolt hullám + boss',
      bullet2: 'Fix S-útvonal — építs, hullám, fejleszt',
      bullet3: 'Nyerés feloldja az Endlesst ezen a pályán',
      length: '~8 perc',
      skill: 'Építési sorrendet edz',
    },
    endless: {
      tagline: 'Alkalmazkodj vagy átszivárog. Nincs vége.',
      bullet1: 'Kampány 1–10, aztán örökké skálázódik',
      bullet2: 'Új hullámmódosító minden hullámon',
      bullet3: 'A futás véget ér, ha a Mag elesik',
      length: '~30 perc',
      skill: 'Hosszú váltásokat edz',
    },
    maze: {
      tagline: 'Te építed a labirintust, amin át kell menniük.',
      bullet1: 'Nyílt mező — nincs előrajzolt út',
      bullet2: 'Tornyok és olcsó falak formálják az utat',
      bullet3: 'Hosszabb út = több idő a tűz alatt',
      length: '~12 perc',
      skill: 'Térbeli útvonalat edz',
    },
    puzzle: {
      tagline: 'Egy felállás nyer. Találd meg.',
      bullet1: '10 pálya, egyetlen megoldás',
      bullet2: 'Fix költségvetés és fix hullám',
      bullet3: 'Látvonalat blokkoló falak + toronylimitek',
      length: '~2 perc / pálya',
      skill: 'Dedukciót edz',
    },
  },
  difficulty: {
    easy: 'KÖNNYŰ',
    normal: 'NORMÁL',
    hard: 'NEHÉZ',
  },
  difficultySub: {
    easy: 'enyhe',
    normal: 'kiegyensúlyozott',
    hard: '✦ {cost}',
  },

  overlay: {
    paused: 'SZÜNET',
    pauseHint: 'NYOMJ  SZÓKÖZT  A  FOLYTATÁSHOZ',

    missionComplete: '// KÜLDETÉS TELJESÍTVE',
    gridSecured: 'HÁLÓZAT BIZTOSÍTVA',
    wavesRepelled: 'Mind a {total} rohamhullám visszaverve.',
    continueEndless: 'FOLYTATÁS  →  ENDLESS',
    unlockEndless: 'nyisd meg az Endlesst a Profilon a folytatáshoz',
    restartCampaign: 'vagy nyomj  R-t  a kampány újrakezdéséhez',

    systemFailure: '// RENDSZERHIBA',
    coreBreached: 'MAG ÁTTÖRVE',
    endlessEnded: 'Endless menet véget ért a {wave}. hullámban.',
    reactorOverrun: 'Reaktort elsöpörték a {wave}. hullámban {total}-ból.',
    runItBack: 'NYOMJ  R-t  AZ  ÚJRAJÁTSZÁSHOZ',

    finalScore: 'VÉGEREDMÉNY',
    newHighScore: '★  ÚJ CSÚCSPONT  ★',
    newEndlessBest: '★  ÚJ ENDLESS REKORD  ★',
    highScore: 'CSÚCSPONT',
    endlessBest: 'ENDLESS REKORD',

    // Sprint 69 — top-banner countdown overlays drawn by render.ts.
    aftershock: "// UTÓRENGÉS — HÁLÓZATI ADÓ  {sec}s //",
    overcharge: "// OVERCHARGE — HÁLÓZAT FELERŐSÍTVE  {sec}s //",
    brownout: "// HÁLÓZAT KIESÉS — TORONYOK LASSÍTVA //",
  },

  profile: {
    title: 'PROFIL',
    subtitle: '// META-FEJLŐDÉS',
    closeHint: 'nyomj  P-t  vagy  ESC-et  a bezáráshoz',
    tabUnlocks: 'FELOLDÁSOK',
    tabScoreboard: 'RANGLISTA',
    flux: 'FLUX',
    unlockTree: 'FELOLDÁSI FA',
    unlockedCount: '{owned} / {total} FELOLDVA',
    lifetime: 'ÖSSZESEN',

    branchArsenal: 'ARZENÁL',
    branchModes: 'MÓDOK',
    branchChallenge: 'KIHÍVÁS',
    branchCosmetic: 'KOZMETIKA',

    statRunsPlayed: 'JÁTSZOTT MENETEK',
    statCampaignWins: 'KAMPÁNY GYŐZELMEK',
    statBestScore: 'LEGJOBB PONT',
    statDeepestEndless: 'LEGMÉLYEBB ENDLESS',
    statTotalFlux: 'ÖSSZES FLUX',

    personalBests: 'EGYÉNI CSÚCSOK',
    waveLabel: '{wave}. HULLÁM',

    runHistory: 'MENET ELŐZMÉNYEK',
    noRuns: 'Még nincs menet — fejezz be egyet és kezdődjön a kapaszkodás.',
    earlierRuns: '+ {n} korábbi menet',
    earlierRunsPlural: '+ {n} korábbi menet',
    colRun: 'MENET',
    colMode: 'MÓD · NEHÉZSÉG',
    colResult: 'EREDMÉNY',
    colOverload: 'OVL',
    colScore: 'PONT',
    colDeltaBest: 'Δ LEGJ.',
    colFlux: 'FLUX',
    starBest: '★ LEGJOBB',

    statusUnlocked: 'FELOLDVA',
    statusOwned: 'TULAJDONOLT',
    statusRequiresPrereq: 'Először {prereq}',
    statusFluxShort: 'Hiányzik ✦{short} Flux',
    statusAffordable: 'Megfizethető — kattints a feloldáshoz',
    statusCost: '✦{cost} a feloldáshoz',
    buyAction: 'VESZ  ▸',
    nodeReq: 'KÖV: {prereq}',

    profileLauncher: 'PROFIL',
    profileKeyHint: '[ P ]',
    viewScoreboard: 'RANGLISTA  MEGNÉZ',
    newPersonalBest: '★  ÚJ EGYÉNI REKORD  ★',
    fromYourBest: 'A LEGJOBBADTÓL',

    grantTower: 'FEGYVERTORONY',
    grantAbility: 'AKTÍV KÉPESSÉG',
    grantMode: 'JÁTÉKMÓD',
    grantDifficulty: 'NEHÉZSÉG',
    grantOverload: 'OVERLOAD {tier}. SZINT',
    grantTheme: 'TÁBLA TÉMA',

    // Sprint 69 — unlock-tree hover preview.
    nodePreview: {
      towerMortar: "Közvetett tűz. HÁTRA tedd az ösvénytől — közelről holttér; lövedékek a klaszterekre, páncélzúzás.",
      towerSniper: "Sortűz látvonalban. Egy hosszú egyenes mentén — minden ellen találatot kap; sarokban gyenge.",
      abilityAirstrike: "Egyszeri robbanás egy mezőre (45c). Klasztert tisztíts vagy nagy ellenséget gyengíts.",
      abilityEmp: "Mezőszéles stun (80c). Mindenkit befagyaszt egy pillanatra, ha a sáv elesik.",
      modeEndless: "Játssz 10. hullám után — procedurális hullámok a Mag bukásáig.",
      modeMaze: "Nyílt mező építkező mód. A tornyok mezőjük blokkolják, A*-utat keresnek.",
      difficultyHard: "Nehezebb hullámok; nagyobb arany- és pontszorzók.",
      overloadCritical: "Overload tier 3 — kapcsold fel a vastagabb pont/Flux szorzókért.",
      overloadMeltdown: "Overload tier 4 — a legkeményebb előre beállított hullám.",
      themeAmber: "Kozmetikai paletta — meleg amber CRT terminál. Tisztán kozmetikai.",
      themePhosphor: "Kozmetikai paletta — vintage zöld foszfor. Tisztán kozmetikai.",
    },
  },

  help: {
    eyebrow: '/ /  R E F E R E N C I A',
    title: 'ÜZEMELTETÉSI KÉZIKÖNYV',
    controls: 'VEZÉRLÉS',
    powerGrid: 'AZ ENERGIAHÁLÓZAT',
    powerGridBody:
      'A fegyverek energiát fogyasztanak; a Pylonok szolgáltatják. Ha a fogyasztás meghaladja az ellátást, a hálózat barnaüzembe esik és minden torony lelassul — építs Pylont, hogy tovább tüzeljenek.',
    objective: 'CÉL',
    objectiveBody:
      'Tisztítsd le mind a 15 kampány-hullámot a győzelemhez; veszítesz, amint a Mag életei nullára esnek. Az Endless mód örökké tart.',
    mazeMode: 'MAZE MÓD',
    mazeModeBody:
      'Egy harmadik mód nyílt mezőn: a tornyok elzárják a csempét, amelyen állnak, és az ellenfelek A*-pályán kerülik meg, így te építed a labirintust. A Magot nem zárhatod el teljesen.',
    glitches: 'GLITCHEK',
    towers: 'TORNYOK',
    abilities: 'KÉPESSÉGEK',
    replayIntro: '↺  INTRO ÚJRA',
    closeHint: 'Esc / H / ? vagy klikk bárhol bezárja',
  },

  intro: {
    eyebrow: '/ /  R E N D S Z E R   E L I G A Z Í T Á S',
    titleBig: 'OVERVOLT',
    subtitle: 'Védd a Magot',
    line1:
      'Sérült programok masíroznak az áramkörön a Magod felé. Építs tornyokat, hogy elpusztítsd őket, mielőtt áttörnek.',
    line2:
      '⚡ Minden torony energiát fogyaszt. Túlépítkezel és a hálózat barnaüzembe esik — minden torony egyszerre lelassul. Építs Pylont, hogy folyjon az energia.',
    line3: 'Építs a nyugodt fázisban, majd nyomd a Hullám indítás-t. Élj túl 15 hullámot a győzelemhez.',
    line4: 'Egér épít · 1–5 választ · Szóköz indít/szünet · U fejleszt · S elad',
    gotIt: 'ÉRTEM  →',
    gotItHint: 'nyomj bármilyen billentyűt',
  },

  puzzle: {
    label: 'PUZZLE',
    levels: {
      'p0-first-light': {
        name: 'Első Fény',
        hint: 'Tegyél egy Pulse-t a folyosó mellé. Bármelyik csempe a hatókörben megteszi.',
      },
      'p1-long-lance': {
        name: 'Hosszú Lándzsa',
        hint: 'Egy Sniper a sávval egy vonalban átszúr minden ellenfelet.',
      },
      'p2-over-the-wall': {
        name: 'Falon Át',
        hint: 'A fal minden közvetlen sugarat blokkol. A mozsárgránátok ívben átrepülnek.',
      },
      'p3-funhouse-mirror': {
        name: 'Görbe Tükör',
        hint: 'Egy Sniper a résen átfűzve mindkét sávot eltalálja.',
      },
      'p4-surplus-riches': {
        name: 'Felesleg Gazdagság',
        hint: 'Építs Pylonokat a szabad sáv fölé. Az Arc Coil a többletből él.',
      },
      'p5-pinch-point': {
        name: 'Szűkület',
        hint: 'Cryo festi a torlaszt; a körülötte lévő Pulse-ok befejezik a csapatot.',
      },
      'p6-cipher-gauntlet': {
        name: 'Cipher Kesztyű',
        hint: 'A mozsár tépi a páncélt; a Cryo festi a kanyart; a Sniper átszúrja az alsó szárat. Használd az egész készletet.',
      },
      'p7-kernel-echo': {
        name: 'Kernel Visszhang',
        hint: 'A mozsarak átdobnak a látvonal-falakon. A Sniper alulról fűzi át. Figyelj a Power Drainre.',
      },
      'p8-overclock-crunch': {
        name: 'Overclock Szorongás',
        hint: 'Egy Pulse nem bírja. Overclockolj a tűzablakban — a tartós sebesség nem elég.',
      },
      'p9-pylon-pinch': {
        name: 'Pylon Szorítás',
        hint: 'Tömörítsd a Pylonokat a tornyaid közé — az aura-korlát szoros építést jutalmaz.',
      },
    },
    briefing: {
      eyebrow: "PUZZLE BRIEFING",
      startBtn: "Indítás ▷",
      cancelBtn: "Mégse",
      sectionConstraints: "KORLÁTOZÁSOK",
      sectionTowers: "TORONYOK",
      sectionWinCondition: "GYŐZELMI FELTÉTEL",
      budgetLabel: "Arany költségvetés",
      wavesLabel: "Hullámok",
      towerAllowed: "ENGEDÉLYEZETT",
      towerForbidden: "ZÁROLT",
      resultEyebrowWon: "PUZZLE TELJESÍTVE",
      resultEyebrowLost: "PUZZLE SIKERTELEN",
      resultTitleWon: "MEGOLDVA!",
      resultTitleLost: "PRÓBÁLD ÚJRA",
      resultStatTowers: "TORONYOK",
      resultStatGold: "ELKÖLTÖTT ARANY",
      resultStatLives: "HÁTRALÉVŐ ÉLET",
      resultNextBtn: "Következő szint ▷",
      resultReplayBtn: "Visszajátszás",
      resultResetBtn: "Kísérlet alaphelyzetbe",
      resultBackBtn: "Vissza a választáshoz",
    },

    // Sprint 69 — puzzle level-select overlay chrome.
    select: {
      subtitle: "Kézzel készített logika szintek — találd ki az egyetlen nyerő elrendezést.",
      close: "Bezárás",
      cardTitle: 'P{index}  {name}',
      lockedCard: "🔒 ZÁROLT",
      solved: "✓ Megoldva",
      unsolved: "▷ Megoldatlan",
      noSolve: "Még nincs megoldás",
      bestLine: "Legjobb: {towers}t · {gold}g · {power}p",
      allowed: "Engedélyezett: {list}",
      parPlaceholder: "par: — / — / —",
      parRow: "{towers}t · {gold}g · {power}p",
    },
  },

  localeChip:
 {
    label: 'NYELV',
    en: 'EN',
    cs: 'CS',
  },

  resume: {
    title: 'Folytatod az előző menetet?',
    resume: 'Folytat',
    newRun: 'Új menet',
    hint: '[Enter] Folytat   ·   [Esc] Új',
    detail: "{mode} · {difficulty} · hullám {wave} · {towers} torony · {gold}g · {lives}♥",
  },

  // -------- Sprint 69: Kernel boss UI chrome ---------------------------------
  boss: {
    kernelHeader: "⚠  A KERNEL",
    phase: "FÁZIS {phase} · {name}",
    powerDrain: "⚡ ENERGIA LEHÚZÁS",
    kernelIncoming: "⚠  KERNEL  ÉRKEZIK  ⚠",
    phaseName: {
      boot: "BOOT SEQUENCE",
      fork: "FORK BOMB",
      panic: "KERNEL PANIC",
    },
  },

  // -------- Sprint 69: Map registry display strings --------------------------
  maps: {
    default: { label: "Datawash", tagline: "Nyitott S-kanyar — a klasszikus OVERVOLT." },
    cramped: { label: "Switchyard", tagline: "Szűk folyosók — minden mező vitatott." },
    lines: { label: "Long Lines", tagline: "Hosszú egyenesek — vezesd a lándzsát." },
    serpentine: { label: "Snake Run", tagline: "Szűk kígyó — a Cryo painter ragyog." },
  },

  // -------- Sprint 69: Ability display names ---------------------------------
  abilities: {
    overcharge: { label: 'Overcharge' },
    airstrike: { label: 'Airstrike' },
    empBurst: { label: 'EMP Burst' },
  },

  // -------- Sprint 69: Overload tier names -----------------------------------
  overload: {
    tierName: {
      nominal: "Nominális",
      charged: "Töltött",
      surging: "Emelkedő",
      critical: "Kritikus",
      meltdown: "Meltdown",
    },
  },

  // -------- Sprint 69: Endless modifier labels -------------------------------
  endlessMod: {
    armoredSurge: { label: "Páncélos roham" },
    fastPack: { label: "Gyors falka" },
    scatteredSpread: { label: "Szóródás" },
    streakRunners: { label: "Streak futók" },
    daemonRush: { label: "Daemon roham" },
  },

  // -------- Sprint 69: Enemy display names (Help roster) ---------------------
  enemies: {
    bit: { label: "Bit" },
    packet: { label: "Packet" },
    daemon: { label: "Daemon" },
    fork: { label: "Fork" },
    cipher: { label: "Cipher" },
    patch: { label: "Patch" },
    buffer: { label: "Buffer" },
    streak: { label: "Streak" },
    kernel: { label: "Kernel" },
  },

  // -------- Sprint 69: Help-panel enemy one-liners ---------------------------
  enemyRole: {
    bit: "Alap ágyútöltelék — olcsó, lassú, törékeny.",
    packet: "Gyors és gyenge; rohan a Magra.",
    daemon: "Nehéz HP szivacs; betörésnél keményen üt.",
    fork: "Két Bitre oszlik megsemmisüléskor.",
    cipher: "Páncélos — szakítsd át Mortar Korrózióval.",
    patch: "A közeli Glitcheket gyógyítja; szedd le elsőként.",
    buffer: "Regenerálódó pajzs; tördd át burst sebzéssel.",
    streak: "Egyedül sprintel; ellenáll a lassításnak.",
    kernel: "Fázis boss — energiát szív. Tördd át a következő fázis előtt.",
  },

  // -------- Sprint 69: Help-panel ability one-liners -------------------------
  abilityRole: {
    overcharge: "Erősítsd a hálót: minden torony gyorsabban tüzel rövid időre. Tervezd nehéz hullámra.",
    airstrike: "Egyszeri robbanás célmezőn. Tisztítsd egy klasztert vagy gyengítsd a nagy ellent.",
    empBurst: "Mezőszéles stun — minden ellenfél rövid időre lefagy. Vegyél időt, ha a sáv elesik.",
  },

  // -------- Sprint 69: Tooltip chrome ----------------------------------------
  tooltip: {
    levelOf: "LV {level}/{max}",
    credits: "{cost} kredit",
    next: "KÖV.  →  LV {level}   {cost} kredit",
    cooldown: "COOLDOWN  {sec}s",
    stat: {
      supply: "ELLÁTÁS  +{n}w a hálóba",
      dmgRngCd: "DMG {damage}    RNG {range}    CD {cd}s",
      pwrChain: "PWR {power}w    LÁNC {chain}",
      pwrAoe: "PWR {power}w    AOE {targets} cél",
      slow: "SLOW ×{factor}  ennyi  {sec}s",
      brittle: "TÖRÉKENY +{pct}% dmg más tornyokból",
      pwrBlast: "PWR {power}w    BLAST {tiles} mező",
      pwrSingle: "PWR {power}w    1 cél",
    },
  },

  // -------- Sprint 69: Help-panel control-row labels -------------------------
  controls: {
    arm: "Válassz Pulse / Arc / Pylon / Cryo / Mortar / Sniper",
    mouse: "Helyezd egy mezőre · kattints egy toronyra a kiválasztáshoz",
    space: "Hullám indítása · hullám szüneteltetése",
    fast: "Előretekerés (1× ⇄ 2×)",
    upgradeSell: "A kiválasztott torony fejlesztése / eladása",
    target: "A kiválasztott torony célzási módjának váltása",
    profile: "Profil megnyitása (feloldás-fa · ranglista)",
    utility: "Mégse · némítás · menet újraindítása",
  },

  // -------- Sprint 69: Unlock-tree node display names ------------------------
  unlocks: {
    towerMortar: { name: "Splash Mortar" },
    towerSniper: { name: "Sniper Rail" },
    abilityAirstrike: { name: "Airstrike" },
    abilityEmp: { name: "EMP Burst" },
    modeEndless: { name: "Endless Mód" },
    modeMaze: { name: "Maze Mód" },
    difficultyHard: { name: "Nehéz nehézség" },
    overloadCritical: { name: "Overload: Critical" },
    overloadMeltdown: { name: "Overload: Meltdown" },
    themeAmber: { name: "Amber Terminál" },
    themePhosphor: { name: "Foszforos Zöld" },
  },

  // Sprint 73 — interaktív oktatóanyag (docs/TUTORIAL_DESIGN.md §6).
  tutorial: {
    button: {
      start: 'OKTATÁS INDÍTÁSA',
      startSub: 'Először itt? Indítsd a túrát →',
      replay: 'TÚRA ÚJRA',
      replaySub: 'Frissítsd az alapokat',
    },
    step: {
      welcome: {
        title: 'Üdvözöl az OVERVOLT',
        body: 'Te egy meghibásodott reaktor védelmi MI-je vagy. Korrupt programok masíroznak az áramkörön a Core felé — pusztítsd el őket, mielőtt átérnek.',
      },
      path: {
        title: 'Az útvonal',
        body: 'Az ellenfelek mindig ezt az S-kanyart követik, a bal oldali spawn mezőtől a jobb oldali Core-ig. Te döntöd el, hova helyezed a tornyokat.',
      },
      firstPlace: {
        title: 'Helyezz el egy tornyot',
        body: 'Egy Pulse Turret 50 kreditbe kerül és 4 áramot fogyaszt. Kattints a világító mezőre az út mellett.',
      },
      wave1: {
        title: 'Indítsd az első hullámot',
        body: 'Nyomd meg a HULLÁM INDÍTÁS-t. A Pulse automatikusan lő mindenre, ami hatótávon belül van.',
      },
      towerRoles: {
        title: 'Öt torony, öt szerep',
        body: 'Pulse: megbízható mindenhol. Sniper: hosszú egyenesek. Mortar: közvetett tűz, HÁTRA az úttól. Arc: áramfeleslegre méreteződik. Cryo: egy szűk pontot lassít és töréskenyé tesz.',
      },
      powerGrid: {
        title: 'Az áramhálózat',
        body: 'Minden torony áramot fogyaszt. Amikor a kereslet meghaladja a kínálatot, a hálózat brownout-ba esik és minden torony lassul. Építs Pylonokat — szomszédos Pylonok növelik a tűzgyorsaságot.',
      },
      overloadDial: {
        title: 'Overload — kockázat vs jutalom',
        body: 'Tekerd a tárcsát feljebb a következő hullámhoz: nehezebb ellenfelek, több Flux. Tekerd le, hogy biztonsággal játssz.',
      },
      overclock: {
        title: 'Túltakold a tornyot',
        body: 'Kapcsold be az OVERCLOCK-ot a kiválasztott tornyon burst ablakhoz: gyorsabb lövés, nagyobb fogyasztás. Burst-nyereség, hosszú távú veszteség.',
      },
      abilities: {
        title: 'Aktív képességek',
        body: 'Három slot: Overcharge feltüzeli a hálózatot. Airstrike tartós perzselt zónát dob. EMP Burst megbénítja az összes ellenfelet. Tervezd meg — ne pánikolj.',
      },
      mazePreview: {
        title: 'Maze mód',
        body: 'Nincs fix útvonal — a tornyok blokkolják a mezőiket és az ellenfelek A*-routálnak körülöttük. Te építed az utat, amin végig kell menniük.',
      },
      puzzlePreview: {
        title: 'Puzzle mód',
        body: 'Kézzel készített logikai pályák látótávolság-falakkal. Fix költségvetés, fix hullám — találd meg az egyetlen nyerő elrendezést. Más játék.',
      },
      wrapUp: {
        title: 'Készen állsz',
        body: 'A futamok FLUX-ot szereznek — költsd el a Profilban új tornyok, képességek és módok feloldására. Kezdd a Campaignnel; térj vissza Endlessért, Maze-ért és Puzzle-ért.',
      },
    },
    ui: {
      eyebrow: 'LÉPÉS {n} / {total}',
      next: 'TOVÁBB →',
      back: '← VISSZA',
      skip: 'ÁTUGRÁS',
      play: 'JÁTÉK  ▸',
      skipConfirm: 'Átugorni a túrát? Bármikor lejátszhatod a címképernyőről.',
    },
  },
};
